import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  EtaConnectionDto,
  EtaConnectionStatusDto,
  EtaConnectionTestResult,
  EtaEnvironment,
  EtaIssuerAddress,
  EtaReadinessResponse,
  EtaTaxMapping,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney } from './money';
import Loading from './Loading';
import { WizardProgress, type WizardStep } from './Wizard';
import Hint from './Hint';

/**
 * ربط منظومة الفاتورة الإلكترونية من الداشبورد — معالج بخطوات لصاحب شركة غير تقنى.
 *
 * الإرسال الفعلى غير موجود هنا: الربط يحفظ بيانات الاعتماد ويختبرها فقط، والإرسال يبقى
 * مطفأ خلف إعداد «إرسال الفاتورة الإلكترونية» حتى الاعتماد والتوقيع الإلكترونى.
 * Client Secret لا يعود من الخادم أبدًا: الشاشة تعرض «محفوظ ✓» وآخر ٤ أحرف فقط.
 */

/**
 * بوابة المنظومة لإنشاء «نظام ERP». BLOCKED-TAX: العنوانان من الموقع المنشور، والمسار
 * داخل البوابة قد يتغيّر — النص إرشاد لا وعد.
 */
const PORTALS: Record<EtaEnvironment, string> = {
  preprod: 'https://preprod.invoicing.eta.gov.eg',
  production: 'https://invoicing.eta.gov.eg',
};

const ENVIRONMENT_LABELS: Record<EtaEnvironment, string> = {
  preprod: 'تجريبية (preprod)',
  production: 'إنتاج',
};

/** حالة الربط بكلمات المالك — نفس النص فى بطاقة اللوحة وفى المعالج. */
export function connectionLabel(status: EtaConnectionStatusDto): {
  text: string;
  hint: string;
  tone: 'off' | 'warn' | 'ok';
} {
  if (status.state !== 'connected') {
    const hint =
      status.state === 'not_configured'
        ? 'لم تُحفظ بيانات الربط بعد.'
        : status.state === 'untested'
          ? 'البيانات محفوظة — بقى «اختبار الاتصال».'
          : 'آخر اختبار اتصال فشل.';
    return { text: 'غير مربوطة', hint, tone: status.state === 'not_configured' ? 'off' : 'warn' };
  }
  if (status.sendingEnabled) {
    return { text: 'مفعّلة', hint: 'الإرسال للمنظومة مفعّل من الإعدادات.', tone: 'ok' };
  }
  return status.environment === 'preprod'
    ? {
        text: 'مربوطة (تجريبى)',
        hint: 'الاتصال ناجح بالبيئة التجريبية، والإرسال مطفأ.',
        tone: 'ok',
      }
    : { text: 'مربوطة (إنتاج)', hint: 'الاتصال ناجح، والإرسال مطفأ حتى الاعتماد.', tone: 'ok' };
}

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    if (caught.problem.status === 503) {
      return 'الخادم لم يُضبط عليه مفتاح تعمية الأسرار (ETA_ENCRYPTION_KEY) — اطلب ذلك ممن يدير الاستضافة.';
    }
    const issues = caught.problem.issues ?? [];
    return issues.length > 0
      ? issues.map((issue) => issue.message).join('، ')
      : (caught.problem.detail ?? caught.problem.title);
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/** بطاقة اللوحة الرئيسية — تُخفى إن لم يكن للمستخدم أن يرى الحالة. */
export function EInvoiceStatusCard({
  companyId,
  onOpen,
}: {
  companyId: string;
  onOpen: () => void;
}) {
  const [status, setStatus] = useState<EtaConnectionStatusDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .eInvoiceStatus(companyId)
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch(() => {
        // البطاقة إضافة للوحة: فشلها لا يُفشل اللوحة
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (!status) return null;
  const label = connectionLabel(status);
  return (
    <button type="button" className="card card-link" onClick={onOpen}>
      <span className="label">الفاتورة الإلكترونية</span>
      <span className={'num eta-' + label.tone}>{label.text}</span>
      <span className="label">{label.hint}</span>
    </button>
  );
}

interface Draft {
  environment: EtaEnvironment;
  clientId: string;
  clientSecret: string;
  activityCode: string;
  branchId: string;
  address: EtaIssuerAddress;
  taxMappings: EtaTaxMapping[];
}

const EMPTY_ADDRESS: EtaIssuerAddress = {
  governate: '',
  regionCity: '',
  street: '',
  buildingNumber: '',
};

function draftOf(connection: EtaConnectionDto): Draft {
  return {
    environment: connection.environment,
    clientId: connection.clientId ?? '',
    clientSecret: '',
    activityCode: connection.activityCode ?? '',
    branchId: connection.branchId,
    address: connection.issuerAddress ?? EMPTY_ADDRESS,
    taxMappings: connection.taxMappings,
  };
}

/** خطوات الربط على نمط المعالج الموحّد. الشرح اختيارى لمن سجّل البرنامج من قبل. */
const STEPS: WizardStep[] = [
  { id: 'portal', title: 'التسجيل فى البوابة', optional: true },
  { id: 'credentials', title: 'بيانات الربط' },
  { id: 'test', title: 'اختبار الاتصال' },
  { id: 'status', title: 'الحالة والجاهزية' },
];

/** المكتمل من حالة الربط على الخادم لا من تنقّل المستخدم: الحفظ ثم الاختبار الناجح. */
export function etaCompleted(
  step: number,
  connection: Pick<EtaConnectionDto, 'secretSaved' | 'state'>,
): Set<string> {
  const done = new Set<string>();
  if (step > 0 || connection.secretSaved) done.add('portal');
  if (connection.secretSaved) done.add('credentials');
  if (connection.state === 'connected') {
    done.add('test');
    done.add('status');
  }
  return done;
}

interface Props {
  companyId: string;
  canManage: boolean;
  canReadReports: boolean;
  onSessionLost: () => void;
}

export default function EInvoiceSetup({
  companyId,
  canManage,
  canReadReports,
  onSessionLost,
}: Props) {
  const [connection, setConnection] = useState<EtaConnectionDto | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [test, setTest] = useState<EtaConnectionTestResult | null>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(messageOf(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    if (!canManage) return;
    api
      .eInvoiceConnection(companyId)
      .then((result) => {
        setConnection(result);
        setDraft(draftOf(result));
        // من سبق له الحفظ يبدأ من حيث يهمه: الحالة، لا شرح التسجيل
        if (result.state === 'connected') setStep(3);
        else if (result.secretSaved) setStep(2);
      })
      .catch(handleError);
  }, [companyId, canManage, handleError]);

  if (!canManage) {
    return (
      <>
        <div className="panel">
          <h2>الفاتورة الإلكترونية</h2>
          <p className="sub">ربط المنظومة يضبطه مالك الشركة. يمكنك متابعة جاهزية الفواتير هنا.</p>
        </div>
        {canReadReports && (
          <EInvoiceReadiness companyId={companyId} onSessionLost={onSessionLost} />
        )}
      </>
    );
  }

  if (!connection || !draft) {
    return (
      <div className="panel">{failure ? <p className="error">{failure}</p> : <Loading />}</div>
    );
  }

  function update(value: Partial<Draft>): void {
    setDraft((current) => (current ? { ...current, ...value } : current));
    setFailure(null);
  }

  const addressFilled = Object.values(draft.address).every((value) => value.trim() !== '');
  const addressEmpty = Object.values(draft.address).every((value) => value.trim() === '');
  const formReady =
    draft.clientId.trim() !== '' &&
    (connection.secretSaved || draft.clientSecret.trim().length >= 8) &&
    /^\d{4}$/.test(draft.activityCode.trim()) &&
    /^\d{1,10}$/.test(draft.branchId.trim()) &&
    (addressFilled || addressEmpty);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!draft || !formReady) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await api.updateEInvoiceConnection(companyId, {
        environment: draft.environment,
        clientId: draft.clientId.trim(),
        ...(draft.clientSecret.trim() ? { clientSecret: draft.clientSecret.trim() } : {}),
        activityCode: draft.activityCode.trim(),
        branchId: draft.branchId.trim(),
        issuerAddress: addressFilled ? draft.address : null,
        taxMappings: draft.taxMappings.filter(
          (mapping) => mapping.taxType.trim() !== '' && mapping.subType.trim() !== '',
        ),
      });
      setConnection(result);
      setDraft(draftOf(result));
      setTest(null);
      setStep(2);
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function runTest(): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setTest(await api.testEInvoiceConnection(companyId));
      const refreshed = await api.eInvoiceConnection(companyId);
      setConnection(refreshed);
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  function mappingFor(ruleId: string): EtaTaxMapping {
    return (
      draft!.taxMappings.find((mapping) => mapping.ruleId === ruleId) ?? {
        ruleId,
        taxType: '',
        subType: '',
      }
    );
  }

  function setMapping(ruleId: string, value: Partial<EtaTaxMapping>): void {
    const others = draft!.taxMappings.filter((mapping) => mapping.ruleId !== ruleId);
    update({ taxMappings: [...others, { ...mappingFor(ruleId), ...value }] });
  }

  const label = connectionLabel({
    state: connection.state,
    environment: connection.environment,
    sendingEnabled: connection.sendingEnabled,
    lastTestAt: connection.lastTest?.at ?? null,
  });

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>ربط منظومة الفاتورة الإلكترونية</h2>
          <span className={'pill eta-' + label.tone}>{label.text}</span>
        </div>
        <WizardProgress
          steps={STEPS}
          current={step}
          completed={etaCompleted(step, connection)}
          onStep={setStep}
          label="خطوات ربط الفاتورة الإلكترونية"
          freeNavigation
        />
        {!connection.encryptionReady && (
          <p className="error">
            الخادم لم يُضبط عليه مفتاح تعمية الأسرار (ETA_ENCRYPTION_KEY)، فلا يمكن حفظ Client
            Secret — اطلب ضبطه ممن يدير الاستضافة.
          </p>
        )}
        {failure && <p className="error">{failure}</p>}
      </div>

      {step === 0 && (
        <div className="panel">
          <h2>الخطوة ١: سجّل البرنامج فى بوابة المنظومة</h2>
          <p>
            المنظومة لا تتعامل مع برامج المحاسبة إلا بعد تسجيل كل برنامج كـ«نظام ERP» من حساب الشركة
            على البوابة. التسجيل يعطيك رقمين تنسخهما هنا: <strong>Client ID</strong> و
            <strong>Client Secret</strong>.
          </p>
          <ol className="eta-guide">
            <li>
              ادخل البوابة بحساب الشركة:{' '}
              <a href={PORTALS.preprod} target="_blank" rel="noreferrer noopener">
                البوابة التجريبية
              </a>{' '}
              (للبداية) أو{' '}
              <a href={PORTALS.production} target="_blank" rel="noreferrer noopener">
                بوابة الإنتاج
              </a>
              .
            </li>
            <li>من إعدادات الممول افتح «أنظمة ERP» وسجّل نظامًا جديدًا باسم يميّز هذا البرنامج.</li>
            <li>انسخ Client ID وClient Secret فور ظهورهما — البوابة قد لا تعرض السر مرة ثانية.</li>
            <li>اعرف كود النشاط (ISIC) المسجّل للشركة، وكود الفرع (٠ للفرع الرئيسى).</li>
          </ol>
          <p className="sub">
            هذا إرشاد عام؛ مسميات القوائم فى البوابة قد تختلف. الربط هنا لا يعنى أن الفواتير صارت
            مطابقة لمتطلبات المصلحة — ذلك قرار بعد مراجعة مستشار الضرائب.
          </p>
          <div className="form-actions">
            <button type="button" className="primary" onClick={() => setStep(1)}>
              معى البيانات — التالى
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <form className="panel" onSubmit={(event) => void save(event)} noValidate>
          <h2>الخطوة ٢: بيانات الربط</h2>
          <div className="form-row">
            <label>
              الرقم الضريبى للشركة
              <input value={connection.taxpayerRin ?? ''} disabled placeholder="غير مسجّل" />
              <span className="label">
                {connection.taxpayerRin
                  ? 'من بيانات الشركة — يُعدَّل من تبويب «الشركة والخيارات».'
                  : 'أضفه أولاً من تبويب «الشركة والخيارات».'}
              </span>
            </label>
          </div>
          <fieldset className="option-group" disabled={busy}>
            <legend>البيئة</legend>
            {(['preprod', 'production'] as const).map((environment) => (
              <label key={environment} className="option">
                <input
                  type="radio"
                  checked={draft.environment === environment}
                  onChange={() => update({ environment })}
                />
                <span>
                  <strong>{ENVIRONMENT_LABELS[environment]}</strong>
                  {environment === 'preprod' && <span className="pill">موصى به للبداية</span>}
                  <span className="label">
                    {environment === 'preprod'
                      ? 'للتجربة قبل الاعتماد — لا أثر رسمى لأى شىء فيها.'
                      : 'البيئة الرسمية. بيانات الربط فيها تختلف عن التجريبية.'}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="form-row">
            <label className="grow">
              Client ID
              <input
                value={draft.clientId}
                maxLength={128}
                autoComplete="off"
                spellCheck={false}
                dir="ltr"
                onChange={(event) => update({ clientId: event.target.value })}
              />
              <Hint>من بوابة الفاتورة الإلكترونية، بعد تسجيل هذا البرنامج كنظام ERP.</Hint>
            </label>
            <label className="grow">
              Client Secret
              <input
                type="password"
                value={draft.clientSecret}
                maxLength={256}
                autoComplete="new-password"
                spellCheck={false}
                dir="ltr"
                placeholder={
                  connection.secretSaved
                    ? 'محفوظ ✓ ••••' + (connection.secretLast4 ?? '') + ' — اتركه فارغًا للإبقاء'
                    : ''
                }
                onChange={(event) => update({ clientSecret: event.target.value })}
              />
              <span className="label">
                {connection.secretSaved
                  ? 'محفوظ ✓ ومُعمّى على الخادم. اكتب سرًا جديدًا فقط لتغييره.'
                  : 'يُحفظ مُعمّى ولا يُعرض مرة أخرى.'}
              </span>
              <Hint>
                يظهر مرة واحدة فى البوابة؛ انسخه فورًا. بعد الحفظ لا يُعرض هنا إلا آخر ٤ أحرف.
              </Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              كود النشاط (٤ أرقام)
              <input
                value={draft.activityCode}
                inputMode="numeric"
                maxLength={4}
                dir="ltr"
                onChange={(event) => update({ activityCode: event.target.value })}
              />
              <Hint>كود النشاط الاقتصادى المسجّل للشركة فى المنظومة.</Hint>
            </label>
            <label>
              كود الفرع
              <input
                value={draft.branchId}
                inputMode="numeric"
                maxLength={10}
                dir="ltr"
                onChange={(event) => update({ branchId: event.target.value })}
              />
              <Hint>٠ للفرع الرئيسى، أو رقم الفرع كما سجّلته فى المنظومة.</Hint>
            </label>
          </div>
          <h3>عنوان الشركة كما فى المنظومة</h3>
          <p className="sub">مطلوب لإرسال الفواتير لاحقًا؛ يمكن تركه الآن واستكماله قبل التفعيل.</p>
          <div className="form-row">
            {(
              [
                ['governate', 'المحافظة'],
                ['regionCity', 'المدينة/المنطقة'],
                ['street', 'الشارع'],
                ['buildingNumber', 'رقم المبنى'],
              ] as const
            ).map(([key, title]) => (
              <label key={key} className={key === 'street' ? 'grow' : undefined}>
                {title}
                <input
                  value={draft.address[key]}
                  maxLength={key === 'street' ? 200 : 100}
                  onChange={(event) =>
                    update({ address: { ...draft.address, [key]: event.target.value } })
                  }
                />
              </label>
            ))}
          </div>
          {!addressFilled && !addressEmpty && (
            <p className="error">أكمل حقول العنوان الأربعة، أو اتركها كلها فارغة.</p>
          )}

          <h3>ربط أنواع الضريبة</h3>
          {connection.taxRules.length === 0 ? (
            <p className="sub">لا توجد قواعد ضريبية معتمدة للشركة بعد — تُربط هنا حين تُضاف.</p>
          ) : (
            <>
              <p className="sub">
                لكل قاعدة ضريبية: نوع الضريبة ونوعها الفرعى فى المنظومة (مثل T1 / V009). يحددهما
                مستشار الضرائب.
              </p>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>القاعدة</th>
                      <th>النسبة</th>
                      <th>نوع الضريبة</th>
                      <th>النوع الفرعى</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connection.taxRules.map((rule) => (
                      <tr key={rule.ruleId}>
                        <td>{rule.nameAr}</td>
                        <td className="num-cell">{rule.ratePercent}%</td>
                        <td>
                          <input
                            value={mappingFor(rule.ruleId).taxType}
                            maxLength={3}
                            dir="ltr"
                            placeholder="T1"
                            onChange={(event) =>
                              setMapping(rule.ruleId, { taxType: event.target.value.toUpperCase() })
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={mappingFor(rule.ruleId).subType}
                            maxLength={16}
                            dir="ltr"
                            placeholder="V009"
                            onChange={(event) =>
                              setMapping(rule.ruleId, { subType: event.target.value.toUpperCase() })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy || !formReady}>
              {busy ? 'جارى الحفظ…' : 'حفظ والانتقال للاختبار'}
            </button>
            <button type="button" className="ghost" disabled={busy} onClick={() => setStep(0)}>
              السابق
            </button>
          </div>
        </form>
      )}

      {step === 2 && (
        <div className="panel">
          <h2>الخطوة ٣: اختبار الاتصال</h2>
          {!connection.secretSaved ? (
            <p className="sub">احفظ بيانات الربط أولاً من الخطوة ٢.</p>
          ) : (
            <>
              <p>
                يتصل البرنامج بخادم الهوية فى البيئة{' '}
                <strong>{ENVIRONMENT_LABELS[connection.environment]}</strong> ويطلب تصريح دخول
                بالبيانات المحفوظة. لا تُرسل أى فاتورة.
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() => void runTest()}
                >
                  {busy ? 'جارى الاختبار…' : 'اختبار الاتصال'}
                </button>
                <button type="button" className="ghost" disabled={busy} onClick={() => setStep(1)}>
                  تعديل البيانات
                </button>
              </div>
              {(test ?? connection.lastTest) && (
                <p className={(test ?? connection.lastTest)!.ok ? 'success' : 'error'}>
                  {(test ?? connection.lastTest)!.ok ? '✓ ' : '✗ '}
                  {(test ?? connection.lastTest)!.message}
                </p>
              )}
              {connection.state === 'connected' && (
                <div className="form-actions">
                  <button type="button" className="primary" onClick={() => setStep(3)}>
                    التالى: الحالة والجاهزية
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <>
          <div className="panel">
            <h2>الخطوة ٤: حالة الربط</h2>
            <p>
              <span className={'pill eta-' + label.tone}>{label.text}</span> {label.hint}
            </p>
            <ul className="eta-summary">
              <li>البيئة: {ENVIRONMENT_LABELS[connection.environment]}</li>
              <li>Client ID: {connection.clientId ?? '—'}</li>
              <li>
                Client Secret:{' '}
                {connection.secretSaved ? 'محفوظ ✓ ••••' + (connection.secretLast4 ?? '') : '—'}
              </li>
              <li>
                آخر اختبار:{' '}
                {connection.lastTest
                  ? new Date(connection.lastTest.at).toLocaleString('ar-EG') +
                    (connection.lastTest.ok ? ' — نجح' : ' — فشل')
                  : 'لم يُختبر'}
              </li>
            </ul>
            <p className="sub">
              إرسال الفواتير للمنظومة مطفأ ويبقى كذلك حتى التوقيع الإلكترونى المعتمد (جهاز
              token/HSM) ومراجعة مستشار الضرائب. التفعيل لاحقًا من تبويب «الشركة والخيارات».
            </p>
          </div>
          {canReadReports && (
            <EInvoiceReadiness companyId={companyId} onSessionLost={onSessionLost} />
          )}
        </>
      )}
    </>
  );
}

const SEVERITY_LABELS = { blocker: 'ناقص', warning: 'تنبيه' } as const;

/** جاهزية الفواتير: ما ينقص كل فاتورة لتُرسل — لا يغيّر شيئًا. */
export function EInvoiceReadiness({
  companyId,
  onSessionLost,
}: {
  companyId: string;
  onSessionLost: () => void;
}) {
  const [data, setData] = useState<EtaReadinessResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ number: string; json: string } | null>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(messageOf(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    api.eInvoiceReadiness(companyId).then(setData).catch(handleError);
  }, [companyId, handleError]);

  async function showDocument(invoiceId: string, number: string): Promise<void> {
    try {
      const result = await api.eInvoiceDocument(companyId, invoiceId);
      setPreview(result.documentJson ? { number, json: result.documentJson } : null);
    } catch (caught) {
      handleError(caught);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>جاهزية الفاتورة الإلكترونية</h2>
        {data && (
          <span className="pill">
            {data.summary.ready} من {data.summary.total} جاهزة
          </span>
        )}
      </div>
      {failure && <p className="error">{failure}</p>}
      {!data && !failure && <Loading />}
      {data && data.companyIssues.length > 0 && (
        <div className="notice">
          <strong>ما ينقص الشركة أو الربط (يمنع كل الفواتير):</strong>
          <ul>
            {data.companyIssues.map((issue) => (
              <li key={issue.code}>
                {SEVERITY_LABELS[issue.severity]}: {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data && data.invoices.length === 0 && <p className="sub">لا فواتير مبيعات بعد.</p>}
      {data && data.invoices.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الفاتورة</th>
                <th>التاريخ</th>
                <th>العميل</th>
                <th>الإجمالى</th>
                <th>الجاهزية</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((invoice) => (
                <tr key={invoice.invoiceId}>
                  <td className="mono">{invoice.number}</td>
                  <td>{invoice.issueDate}</td>
                  <td>{invoice.customerNameAr}</td>
                  <td className="num-cell">{formatMoney(invoice.total)}</td>
                  <td>
                    {invoice.ready ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => void showDocument(invoice.invoiceId, invoice.number)}
                      >
                        ✓ جاهزة — معاينة الحمولة
                      </button>
                    ) : null}
                    {invoice.issues.length > 0 && (
                      <ul className="eta-issues">
                        {invoice.issues.map((issue, index) => (
                          <li key={issue.code + index} className={'eta-' + issue.severity}>
                            {SEVERITY_LABELS[issue.severity]}: {issue.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {preview && (
        <div className="eta-preview">
          <div className="panel-head">
            <h3>حمولة الفاتورة {preview.number} (غير موقّعة — لا تُرسل)</h3>
            <button type="button" className="ghost" onClick={() => setPreview(null)}>
              إغلاق
            </button>
          </div>
          <pre dir="ltr">{preview.json}</pre>
        </div>
      )}
    </div>
  );
}
